/* eslint-disable no-param-reassign */
import _compose from 'lodash/fp/compose';

const invariant = require('invariant');

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const timeIntervals = {
  days: DAY,
  hours: HOUR,
  minutes: MINUTE,
  seconds: SECOND,
};

export const getBeginOfDateInUTC = (date) => {
  invariant(date instanceof Date, 'Date instance should be passed');

  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));
};

export const cleanUTCTime = (date) => {
  invariant(date instanceof Date, 'Date instance should be passed');
  date.setUTCHours(0);
  date.setUTCMinutes(0);
  date.setUTCSeconds(0);
  date.setUTCMilliseconds(0);

  return date;
};

export const convertToUTC = (date) => {
  if (typeof date === 'string') {
    date = new Date(`${date}Z`);
  }

  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

export const convertUTCToLocal = (date) => {
  if (typeof date === 'string') {
    date = new Date(date);
  }

  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    )
  );
};

export const toMs = (date) => {
  if (typeof date.getMonth !== 'function') {
    date = new Date(date);
  }

  return date.getTime();
};

export const isAfter = (first, second) => {
  first = toMs(first);
  second = toMs(second);

  return first > second;
};

export const isSame = (first, second, option = 'days') => {
  if (first === second) {
    return true;
  }
  first = toMs(first);
  second = toMs(second);

  return Math.floor(first / timeIntervals[option]) === Math.floor(second / timeIntervals[option]);
};

// is two time ranges overlap
export const isOverlap = (rangeBeginA, rangeEndA, rangeBeginB, rangeEndB) => {
  const aBegin = toMs(rangeBeginA);
  const aEnd = toMs(rangeEndA);

  const bBegin = toMs(rangeBeginB);
  const bEnd = toMs(rangeEndB);

  if (
    Math.min(aBegin, aEnd) <= Math.max(bBegin, bEnd) &&
    Math.max(aBegin, aEnd) >= Math.min(bBegin, bEnd)
  ) {
    return true;
  }

  return false;
};

export const diff = (first, second, option = 'days') => {
  first = toMs(first);
  second = toMs(second);

  return Math.floor((second - first) / timeIntervals[option]);
};

export const add = (date, toAdd, option = 'days') => {
  date = toMs(date);

  return new Date(date + toAdd * timeIntervals[option]);
};

export const addUTC = (date, toAdd, option = 'days') => {
  date = toMs(date);

  return convertToUTC(new Date(date + toAdd * timeIntervals[option]));
};

const oneDigitHour = /^(\d):/;
const oneDigitMinute = /:(\d)$/;

export const addLeadingZeroesToHMString = (timeString) => {
  return timeString.replace(oneDigitHour, '0$1:').replace(oneDigitMinute, ':0$1');
};

export function getDateSave(date) {
  if (typeof date.getMonth !== 'function') {
    date = new Date(date);
  }

  return date;
}

export const formatISO = (date) => {
  date = getDateSave(date);
  if (Number.isNaN(date)) {
    return '';
  }

  const dateWithTimezone = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  return dateWithTimezone.toISOString().substring(0, 10);
};

export const formatShort = (date, delim = '.') => {
  date = getDateSave(date);

  if (Number.isNaN(date)) {
    return '';
  }

  return [`0${date.getDate()}`.slice(-2), `0${date.getMonth() + 1}`.slice(-2)].join(delim);
};

export const formatShortUTC = (date, delim = '.') => {
  date = getDateSave(date);

  if (Number.isNaN(date)) {
    return '';
  }

  return [`0${date.getUTCDate()}`.slice(-2), `0${date.getUTCMonth() + 1}`.slice(-2)].join(delim);
};

export const parseISOToLocal = (dateString) => {
  const startTime = new Date(dateString);

  return new Date(startTime.getTime() + startTime.getTimezoneOffset() * 60000);
};

// YYYYMMDD
export const parseServerDate = (dateString) => {
  const Day = dateString.substring(6);
  const Month = dateString.substring(4, 6);
  const Year = dateString.substring(0, 4);

  return new Date(Year, Month - 1, Day);
};

// YYYYMMDD
export const parseServerDateUTC = (dateString) => {
  const Day = dateString.substring(6);
  const Month = dateString.substring(4, 6);
  const Year = dateString.substring(0, 4);

  return new Date(Date.UTC(+Year, Month - 1, +Day));
};

// DDMMYYYY or MMDDYYYY
export const parseDateString = (dateString, isMonthFirst) => {
  if (!dateString || dateString.length !== 8) {
    return;
  }

  const matches = /^(\d{2})(\d{2})(\d{4})$/.exec(dateString) || [];

  const d = matches[isMonthFirst ? 2 : 1];
  const m = matches[isMonthFirst ? 1 : 2] - 1;
  const y = matches[3];

  const date = new Date(y, m, d);
  const isValid = date.getDate() === +d && date.getMonth() === +m && date.getFullYear() === +y;

  // eslint-disable-next-line consistent-return
  return isValid ? date : undefined;
};

// DDMMYYYY or MMDDYYYY
export const parseDateStringUTC = (dateString, isMonthFirst) => {
  if (!dateString || dateString.length !== 8) {
    return;
  }

  const matches = /^(\d{2})(\d{2})(\d{4})$/.exec(dateString) || [];

  const d = +matches[isMonthFirst ? 2 : 1];
  const m = +matches[isMonthFirst ? 1 : 2] - 1;
  const y = +matches[3];

  const date = new Date(Date.UTC(y, m, d));
  const isValid =
    date.getUTCDate() === d && date.getUTCMonth() === m && date.getUTCFullYear() === y;

  // eslint-disable-next-line consistent-return
  return isValid ? date : undefined;
};

export const checkZero = (num) => {
  return num < 10 ? `0${num}` : num;
};

export const stringifyDate = (date) => {
  const day = checkZero(date.getDate());
  const month = checkZero(date.getMonth() + 1);
  const year = date.getFullYear();

  return `${year}${month}${day}`;
};

export const stringifyDateUTC = (date) => {
  const day = checkZero(date.getUTCDate());
  const month = checkZero(date.getUTCMonth() + 1);
  const year = date.getUTCFullYear();

  return `${year}${month}${day}`;
};

export const stringifyDateReverse = (date) => {
  const day = checkZero(date.getDate());
  const month = checkZero(date.getMonth() + 1);
  const year = date.getFullYear();

  return `${day}${month}${year}`;
};

export const parseTimeString = (timeString) => {
  const hours = timeString.slice(0, 2);
  const minutes = timeString.slice(2, 4);

  return {
    hours,
    minutes,
  };
};

export const getDateTime = (dateStr, timeString) => {
  const date = parseServerDate(dateStr);
  const { hours, minutes } = parseTimeString(timeString);
  date.setHours(hours);
  date.setMinutes(minutes);

  return date;
};

export const getUTCDateTime = (dateString, timeString) => {
  const date = parseServerDateUTC(dateString);
  const { hours, minutes } = parseTimeString(timeString);
  date.setUTCHours(hours);
  date.setUTCMinutes(minutes);

  return date;
};

export const getFormatedDate = (lang, date, options) => {
  return Intl.DateTimeFormat(lang, options).format(date);
};

export const parseDateToTime = (date) => {
  date = convertToUTC(date);

  return {
    hours: checkZero(date.getHours()),
    minutes: checkZero(date.getMinutes()),
  };
};

export const calculateAge = (date, today = new Date()) => {
  if (!date) {
    return 0;
  }

  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDay = today.getDate();
  const dateMonth = date.getMonth();

  let age = todayYear - date.getFullYear();

  if (todayMonth < dateMonth) {
    age--;
  }

  if (dateMonth === todayMonth && todayDay < date.getDate()) {
    age--;
  }

  return age;
};

export const calculateAgeUTC = (date, today = new Date()) => {
  if (!date) {
    return 0;
  }

  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDay = today.getDate();
  const dateMonth = date.getUTCMonth();

  let age = todayYear - date.getUTCFullYear();

  if (todayMonth < dateMonth) {
    age--;
  }

  if (dateMonth === todayMonth && todayDay < date.getUTCDate()) {
    age--;
  }

  return age;
};

export const daysChanged = (startdate, enddate) => {
  let dayCount = 0;
  if (startdate.getDate() === enddate.getDate()) {
    return dayCount;
  }
  // copy and clear hours, minuts, seconds ..
  const startDate = new Date(startdate.getYear(), startdate.getMonth(), startdate.getDate());
  const endDate = new Date(enddate.getYear(), enddate.getMonth(), enddate.getDate());
  while (endDate > startDate) {
    dayCount++;
    startDate.setDate(startDate.getDate() + 1);
  }

  return dayCount;
};

export const isDateBetween = (minDate, date, maxDate) => {
  return minDate.getTime() <= date.getTime() && date.getTime() <= maxDate.getTime();
};

const dateSorterMax = (firstDate, secondDate) =>
  !!firstDate && !!secondDate && firstDate.getTime() <= secondDate.getTime();
const dateSorterMin = (firstDate, secondDate) =>
  !!firstDate && !!secondDate && firstDate.getTime() >= secondDate.getTime();
const extremumDate = function (...params) {
  const sorter = params[0] === 'max' ? dateSorterMax : dateSorterMin;

  return [...params].slice(1).sort(sorter)[0];
};

export function lastDayOfMonth(year, month) {
  return new Date(new Date(year, month + 1) - 1);
}

// DDMMYYYY or MMDDYYYY
export const isValidDate = function (date, isMonthFirst = false) {
  if (!date || date.length !== 8) {
    return false;
  }

  /* eslint-disable */
  const d = +matches[isMonthFirst ? 2 : 1];
  const m = +(matches[isMonthFirst ? 1 : 2] - 1);
  const y = +matches[3];
  /* eslint-enable */

  const composedDate = new Date(y, m, d);

  return (
    composedDate.getDate() === d &&
    composedDate.getMonth() === m &&
    composedDate.getFullYear() === y
  );
};

export const isValidUTCDate = function (date, isMonthFirst = false) {
  if (!date || date.length !== 8) {
    return false;
  }
  /* eslint-disable */
  const d = +matches[isMonthFirst ? 2 : 1];
  const m = +(matches[isMonthFirst ? 1 : 2] - 1);
  const y = +matches[3];
  /* eslint-enable */

  const composedDate = new Date(Date.UTC(y, m, d));

  return (
    composedDate.getUTCDate() === d &&
    composedDate.getUTCMonth() === m &&
    composedDate.getUTCFullYear() === y
  );
};

export const formatDateString = function (date, isMonthFirst) {
  if (!date || date.length !== 8) {
    return;
  }

  // eslint-disable-next-line consistent-return
  return date.replace(/(\d{2})(\d{2})(\d{4})/, isMonthFirst ? '$2.$1.$3' : '$1.$2.$3');
};

// DDMMYYYY or MMDDYYYY -> YYYYMMDD
export const makeServerDate = function (date, isMonthFirst) {
  return date.replace(/(\d{2})(\d{2})(\d{4})/, isMonthFirst ? '$3$1$2' : '$3$2$1');
};

export const convertISOToLocalString = (dateString, lang) => {
  const separator = lang === 'en-us' ? '/' : '.';

  if (!dateString) return null;
  const dateArr = dateString.slice(0, 10).split('-');
  if (lang === 'en-us') {
    const year = dateArr.shift();
    dateArr.push(year);
  } else {
    dateArr.reverse();
  }

  return dateArr.join(separator);
};

export const serverDateToLocalString = (lang, date) => {
  const parsedDate = new Date(parseServerDate(date));

  return getFormatedDate(lang, parsedDate, {
    day: 'numeric',
    month: 'long',
  });
};

export const getParsedDateFormatter = function (lang, intlParams) {
  return _compose(
    (x) => x.replace('.', ''),
    Intl.DateTimeFormat(lang, {
      timeZone: 'UTC',
      ...intlParams,
    }).format,
    (date) =>
      date instanceof Date
        ? _compose(
            (x) => new Date(x),
            (x) => formatISO(x)
          )(date)
        : new Date(date)
  );
};

export default {
  diff,
  isAfter,
  isSame,
  isOverlap,
  add,
  addUTC,
  cleanUTCTime,
  addLeadingZeroesToHMString,
  formatISO,
  parseISOToLocal,
  formatShort,
  formatShortUTC,
  parseServerDate,
  parseServerDateUTC,
  makeServerDate,
  parseDateString,
  parseDateStringUTC,
  formatDateString,
  parseTimeString,
  getDateTime,
  getUTCDateTime,
  stringifyDate,
  stringifyDateUTC,
  stringifyDateReverse,
  parseDateToTime,
  calculateAge,
  calculateAgeUTC,
  convertToUTC,
  daysChanged,
  extremumDate,
  lastDayOfMonth,
  isDateBetween,
  getFormatedDate,
  isValidDate,
  isValidUTCDate,
  convertISOToLocalString,
  serverDateToLocalString,
  getBeginOfDateInUTC,
  getParsedDateFormatter,
  convertUTCToLocal,
};
